import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import cantileverThinkingGif from 'assets/cantilever-thinking.gif'
import {
  getDocChatResponseStatusText,
  isDocChatSearchPlanThought,
  shouldShowDocChatSearchStatus,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatText'
import type {
  DocChatResponseEvent,
  DocChatThoughtEvent,
} from 'features/knowledgeDocumentViewer/types'
import { CircleCheck, CircleDot } from 'lucide-react'
import React, { useEffect, useId, useState } from 'react'

import {
  ExpandedThoughtConnector,
  ExpandedThoughtIcon,
  ExpandedThoughtIconColumn,
  ExpandedThoughtRow,
  ExpandedThoughtText,
  ThinkingGif,
  ThoughtsChevron,
  ThoughtsExpanded,
  ThoughtsSummary,
  ThoughtsToggle,
} from './docChatStyles'

function getThoughtSummary({
  isThinking,
  isSavingResponse,
  responseEvent,
}: {
  isThinking: boolean
  isSavingResponse: boolean
  responseEvent?: DocChatResponseEvent
}): string {
  if (isSavingResponse) {
    return t`Saving response...`
  }
  if (isThinking) {
    return t`Thinking...`
  }
  if (responseEvent) {
    return getDocChatResponseStatusText(responseEvent)
  }
  return t`Thoughts`
}

const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)'

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY)
    const onChange = () => {
      setPrefersReducedMotion(mediaQuery.matches)
    }
    onChange()
    mediaQuery.addEventListener('change', onChange)
    return () => mediaQuery.removeEventListener('change', onChange)
  }, [])

  return prefersReducedMotion
}

function ThinkingIndicator(): React.ReactElement | null {
  const prefersReducedMotion = usePrefersReducedMotion()
  if (prefersReducedMotion) return null
  return (
    <ThinkingGif
      src={cantileverThinkingGif}
      alt=''
      aria-hidden='true'
      draggable={false}
    />
  )
}

function ExpandedThoughtItem({
  children,
  isLast,
  title,
}: {
  children: React.ReactNode
  isLast: boolean
  title?: string
}): React.ReactElement {
  return (
    <ExpandedThoughtRow>
      <ExpandedThoughtIconColumn aria-hidden='true'>
        <ExpandedThoughtIcon>
          {isLast ? <CircleCheck size={12} /> : <CircleDot size={12} />}
        </ExpandedThoughtIcon>
        {isLast ? null : <ExpandedThoughtConnector />}
      </ExpandedThoughtIconColumn>
      <ExpandedThoughtText $isLast={isLast} title={title}>
        {children}
      </ExpandedThoughtText>
    </ExpandedThoughtRow>
  )
}

export function DocChatCollapsibleThoughts({
  isThinking,
  isSavingResponse = false,
  responseEvent,
  thoughts,
}: {
  isThinking: boolean
  isSavingResponse?: boolean
  responseEvent?: DocChatResponseEvent
  thoughts: DocChatThoughtEvent[]
}): React.ReactElement | null {
  useLingui()
  const [isOpen, setIsOpen] = useState(false)
  const contentId = useId()
  const visibleThoughts = thoughts.filter(
    (thought) => !isDocChatSearchPlanThought(thought)
  )
  const summary = getThoughtSummary({
    isThinking,
    isSavingResponse,
    responseEvent,
  })
  const isFallback = responseEvent?.metadata.is_fallback === true
  const showResponseStatus =
    responseEvent !== undefined &&
    !isFallback &&
    shouldShowDocChatSearchStatus(responseEvent)
  const canExpand = visibleThoughts.length > 0 || showResponseStatus

  if (!canExpand && !isThinking && !isSavingResponse) return null

  return (
    <div>
      <ThoughtsToggle
        type='button'
        aria-controls={canExpand ? contentId : undefined}
        aria-expanded={canExpand ? isOpen : undefined}
        aria-live='polite'
        disabled={!canExpand}
        onClick={() => {
          if (!canExpand) return
          setIsOpen((open) => !open)
        }}>
        {isThinking ? <ThinkingIndicator /> : null}
        <ThoughtsSummary>{summary}</ThoughtsSummary>
        {isThinking || !canExpand ? null : (
          <ThoughtsChevron $open={isOpen} aria-hidden='true' size={14} />
        )}
      </ThoughtsToggle>
      {canExpand && isOpen ? (
        <ThoughtsExpanded id={contentId}>
          {visibleThoughts.map((thought, index) => (
            <ExpandedThoughtItem
              key={thought.id}
              isLast={
                !showResponseStatus && index === visibleThoughts.length - 1
              }>
              {thought.message}
            </ExpandedThoughtItem>
          ))}
          {showResponseStatus ? (
            <ExpandedThoughtItem
              isLast
              title={responseEvent.metadata.query_plan.elasticsearch_query}>
              {getDocChatResponseStatusText(responseEvent)}
            </ExpandedThoughtItem>
          ) : null}
        </ThoughtsExpanded>
      ) : null}
    </div>
  )
}
